import type { SortOrder } from 'src/types/pagination';
import type { AuditTrailDTO, EntityType } from 'src/types/dto/audit-trail.dto';
import type { GridSortModel, GridPaginationModel, GridRenderCellParams } from '@mui/x-data-grid';

import dayjs from 'dayjs';
import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Tooltip, Typography } from '@mui/material';
import { gridClasses } from '@mui/x-data-grid';
import { AppDataGrid } from 'src/components/app-data-grid';


import { usePaginatedApi } from 'src/hooks/use-paginated-api';
import { useUserPermissions } from 'src/hooks/use-user-permissions';

import { endpoints } from 'src/utils/axios';
import { convertToTimezone } from 'src/utils/timezone';

import { useTranslate } from 'src/locales';

import { EmptyContent } from 'src/components/empty-content';
import { EntityLinkManager } from 'src/components/EntityLinkManager';
import { ExportButton } from 'src/components/export-button/index.';

import { DEFAULT_PAGE_SIZE, PAGINATION_OPTIONS } from 'src/types/pagination';
import { PermissionKeys } from 'src/auth/types';

import { exportAuditTrails } from 'src/actions/audit-trails';
import { toast } from 'src/components/snackbar';

import { AuditTrailFilters } from './components/audit-trail-filters';

type EntityFilter = [EntityType, number];
interface AuditTrailsTabProps {
  entityFilters?: EntityFilter[];
  hideEntityTypesFilter?: boolean;
  hideIdColumn?: boolean;
  hideSearchAndFilters?: boolean;
  endpoint?: string;
  projectName?: string;
}

export function AuditTrailsTab({ entityFilters,projectName, hideEntityTypesFilter = false, hideIdColumn = false, hideSearchAndFilters = false, endpoint = endpoints.auditTrails.get }: AuditTrailsTabProps) {
  const { t,i18n  } = useTranslation('organization');
  const { currentLang } = useTranslate();
  const { hasAnyPermission } = useUserPermissions();
  const loggedInUserTimezone = dayjs.tz.guess();
  const [dateRange, setDateRange] = useState<{
    startDate: string;
    endDate: string;
  } | null>(null);

  const columns = [
    {
      field: 'eventSummary',
      headerName: t('audit-trails.event'),
      flex: 2,
      minWidth: 200,
      sortable: false,
      renderCell: (params: GridRenderCellParams<AuditTrailDTO>) => (
        <Tooltip title={params.row.eventSummary}>
          <Typography
            variant="body2"
            sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {params.row.eventSummary}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'user',
      headerName: t('audit-trails.user'),
      valueFormatter: (value: string, row: AuditTrailDTO) => row?.user?.name || '-',
      flex: 1,
    },
    ...(hideIdColumn ? [] : [{
      field: 'entityName',
      headerName: t('audit-trails.entityId'),
      flex: 1,
      sortable: false,
      renderCell: (params: GridRenderCellParams<AuditTrailDTO>) => (
        <EntityLinkManager
          disabled={params.row.action === 'DELETE' || !(params.row.entityId && (params.row.entityName || params.row.meta?.entityName))}
          entityId={params.row.entityId}
          entityType={params.row.entityType}
          tabId="audit-trails"
          label={params.row.meta?.entityName ?? params.row.entityName}
          onClose={() => {
            mutate();
          }}
        />
      ),
    }]),
    {
      field: 'action',
      headerName: t('audit-trails.eventType'),
      valueFormatter: (value: string, row: AuditTrailDTO) => value && i18n.exists(`organization:audit-trails.events.${value}`) ? t(`organization:audit-trails.events.${value}`) : value !== undefined && value !== null ? value : '-',
      flex: 1,
      minWidth: 120,
    },
    {
      field: 'createdAtUtc',
      headerName: t('audit-trails.timestampUtc'),
      valueFormatter: (value: string, row: AuditTrailDTO) =>
        dayjs.utc(row.createdAt).format('DD MMM YY, HH:mm'),
      flex: 1,
      minWidth: 160,
    },
    {
      field: 'createdAt',
      headerName: t('audit-trails.timestampLocal'),
      valueFormatter: (value: string) =>
        convertToTimezone(value, loggedInUserTimezone).format('DD MMM YY, HH:mm'),
      flex: 1,
      minWidth: 160,
    },
    {
      field: 'ipAddress',
      headerName: t('audit-trails.ipAddress'),
      valueFormatter: (value: string) => value,
      flex: 1,
      minWidth: 120,
    },
    {
      field: 'oldNew',
      headerName: t('audit-trails.oldNew'),
      flex: 1,
      minWidth: 120,
      sortable: false,
      renderCell: (params: GridRenderCellParams<AuditTrailDTO>) => (
        <Tooltip title={params.row.oldNew}>
          <Typography
            sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            variant="body2"
          >
            {params.row.oldNew}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'triggeredBy',
      headerName: t('audit-trails.triggeredByLabel'),
      valueFormatter: (value: string) => (value ? i18n.exists(t(`audit-trails.triggeredBy.${value}`))?t(`audit-trails.triggeredBy.${value}`):value : ''),
      flex: 1,
      minWidth: 120,
    },
    {
      field: 'project',
      headerName: t('audit-trails.project'),
      valueFormatter: (value: string, row: AuditTrailDTO) => row?.meta?.projectOrderReference || '-',
      flex: 1,
      minWidth: 120,
      sortable: false,
    },
    {
      field: 'customer',
      headerName: t('audit-trails.customer'),
      valueFormatter: (value: string, row: AuditTrailDTO) => row?.meta?.customerName || '-',
      flex: 1,
      minWidth: 120,
      sortable: false,
    },
  ];
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 50,
  });

  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'createdAt', sort: 'desc' }]);
  const [filters, setFilters] = useState<Record<string, string[]>>({});
  const [search, setSearch] = useState('');

  // Export permissions check
  const canExport = useMemo(
    () =>
      hasAnyPermission([
        {
          permission: PermissionKeys.VIEW_AUDIT_TRAILS,
          levels: ['GROUP', 'ALL'],
        },
      ]),
    [hasAnyPermission]
  );
  const { data, isLoading, pagination, mutate } = usePaginatedApi<AuditTrailDTO>({
    endpoint: endpoint,
    paginationParams: { page: paginationModel.page + 1, take: paginationModel.pageSize },
    filterParams: {
      sortOptions: sortModel.map((e) => ({
        field: e.field,
        order: e.sort?.toUpperCase() as SortOrder,
      })),
      search,
    },
    additionalParams: {
      language: currentLang.value,
      ...(filters.entityTypes?.length > 0
        ? {
            entityTypes: filters.entityTypes.join(','),
          }
        : {}),
      ...(filters.actionTypes?.length > 0
        ? {
            actionTypes: filters.actionTypes.join(','),
          }
        : {}),
      ...(entityFilters && entityFilters.length > 0
        ? {
            entityFilters: JSON.stringify(entityFilters),
          }
        : {}),
      ...(filters.userIds?.length > 0
        ? {
            userIds: filters.userIds.join(','),
          }
        : {}),
      ...(dateRange
        ? {
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
          }
        : {}),
    },
  });

  // Export function
  const handleExport = useCallback(async () => {
    try {
      const exportParams: any = {
        page: paginationModel.page + 1,
        take: paginationModel.pageSize,
        language: currentLang.value,
      };

      if (search) {
        exportParams.search = search;
      }

      if (filters.entityTypes?.length > 0) {
        exportParams.entityTypes = filters.entityTypes.join(',');
      }
      if (filters.actionTypes?.length > 0) {
        exportParams.actionTypes = filters.actionTypes.join(',');
      }
      if (entityFilters && entityFilters.length > 0) {
        exportParams.entityFilters = JSON.stringify(entityFilters);
      }
      if (filters.userIds?.length > 0) {
        exportParams.userIds = filters.userIds.join(',');
      }
      if (dateRange) {
        exportParams.startDate = dateRange.startDate;
        exportParams.endDate = dateRange.endDate;
      }

      const blob = await exportAuditTrails(exportParams);

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit_trails_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();

      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success(t('audit-trails.export.success') || 'Audit trails exported successfully');
    } catch (error) {
      console.error('Error exporting audit trails:', error);
      toast.error(t('audit-trails.export.error') || 'Failed to export audit trails');
    }
  }, [paginationModel, search, filters, entityFilters, dateRange, currentLang.value, t]);

  return (
    <>
      {!hideSearchAndFilters && (
        <Box sx={{ width: '100%', mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ flex: 1 }}>
            <AuditTrailFilters
              onFiltersChange={setFilters}
              onSearchChange={setSearch}
              onDateRangeChange={setDateRange}
              hideEntityTypesFilter={hideEntityTypesFilter}
            />
          </Box>
          {canExport && (
            <Box sx={{ ml: 2 }}>
              <ExportButton onExport={handleExport} />
            </Box>
          )}
        </Box>
      )}
      <AppDataGrid
        checkboxSelection={false}
        disableRowSelectionOnClick
        disableColumnFilter
        rows={data || []}
        getRowId={(row) => row.id}
        columns={columns}
        loading={isLoading}
        columnHeaderHeight={32}
        rowHeight={40}
        pageSizeOptions={[50]}
        paginationModel={paginationModel}
        rowCount={pagination?.itemCount || 0}
        paginationMode="server"
        onPaginationModelChange={(newModel) => {
          setPaginationModel(newModel);
        }}
        sortModel={sortModel}
        onSortModelChange={(newModel) => {
          setSortModel(newModel);
        }}
        hideFooterPagination={(pagination?.itemCount || 0) <= 50}
        hideFooter={(pagination?.itemCount || 0) <= 50}
        slots={{
          noRowsOverlay: () => <EmptyContent title={t('common:list.empty')} />,
          noResultsOverlay: () => <EmptyContent title={t('common:list.empty')} />,
        }}
        sx={{
          '.MuiDataGrid-columnHeader:focus-within, .MuiDataGrid-cell:focus-within': {
            outline: 'none',
          },
          [`& .${gridClasses.cell}`]: {
            alignItems: 'center',
            display: 'inline-flex',
            padding: '8px',
            borderBottomStyle: 'solid',
          },
          [`& .${gridClasses.columnHeader}`]: {
            borderBottomStyle: 'solid',
          },
        }}
      />
    </>
  );
}
